import compact from 'lodash/compact'
import includes from 'lodash/includes'
import kebabCase from 'lodash/kebabCase'
import map from 'lodash/map'
import trimEnd from 'lodash/trimEnd'
import dynamic from 'next/dynamic'
import React, { ReactNode, useMemo } from 'react'

import { ProductSliderBlockData } from '@Components/CMS/Blocks/ProductSliderBlock'
import SplitLine from '@Components/CMS/SplitLine'
import Trustpilot from '@Components/CMS/Trustpilot'
import {
  AccordionListBlock as AccordionListBlockData,
  ActiveCampaignBlock as ActiveCampaignBlockData,
  Block,
  BlockTypeWithGlobal,
  GalleryBlock as GalleryBlockData,
  GlobalBlock,
  InfoPanelBlock as InfoPanelBlockData,
  LookDisplayBlock as LookDisplayBlockData,
  LookListBlock as LookListBlockData,
  PageGridBlock as PageGridBlockData,
  PagePresentation,
  PageSliderBlock as PageSliderBlockGraphData,
  ProductGridSelectorBlock as ProductGridSelectorBlockData,
  ProductListBlock as ProductListBlockData,
  SplitTestGroup,
  UspBlock,
} from '@Generated/graphql'
import { BlockContext } from '@Globals/CMS/Context'
import { Experiment } from '@Globals/SplitTests/Experiment'
import { Test } from '@Globals/SplitTests/types'

interface BlockOptions {
  id: string
  idPrefix: string
  ids: Record<string, number>
  index: number
  isInModal: boolean
  isInjection?: boolean
  presentation: PagePresentation
}

type BlockToComponentMap = {
  [key: string]: (block: BlockTypeWithGlobal, options: BlockOptions) => ReactNode
}

const getBlockID = (block: BlockTypeWithGlobal) => `${kebabCase(block.type || '').replace('-block', '')}-${block.id}`

const AccordionListBlock = dynamic(() => import('@Components/CMS/Blocks/AccordionListBlock'))
const ActiveCampaignBlock = dynamic(() => import('@Blocks/ActiveCampaignBlock'))
const BuyButtonBlock = dynamic(() => import('@Components/CMS/Blocks/BuyButtonBlock'))
const CategoryListBlock = dynamic(() => import('@Blocks/CategoryListBlock'))
const ClubSignupBlock = dynamic(() => import('@Blocks/ClubSignup'))
const EmbedCodeBlock = dynamic(() => import('@Components/CMS/Blocks/EmbedCodeBlock'))
const FactBlock = dynamic(() => import('@Components/CMS/Blocks/FactBlock'))
const GalleryBlock = dynamic(() => import('@Components/CMS/Blocks/GalleryBlock'))
const GiftCardBlock = dynamic(() => import('@Components/CMS/Blocks/GiftCardBlock'))
const InfoPanelBlock = dynamic(() => import('@Components/CMS/Blocks/InfoPanelBlock/InfoPanelBlock'))
const ImageBlock = dynamic(() => import('@Components/CMS/Blocks/ImageBlock'))
const ImageGridBlock = dynamic(() => import('@Components/CMS/Blocks/ImageGridBlock'))
const LeadMagnetBlock = dynamic(() => import('@Components/CMS/Blocks/LeadMagnetBlock'))
const LinkBlock = dynamic(() => import('@Components/CMS/Blocks/LinkBlock'))
const LookDisplayBlock = dynamic(() => import('@Blocks/LookDisplay'))
const LookListBlock = dynamic(() => import('@Blocks/LookList'))
const TagsBlock = dynamic(() => import('@Components/CMS/Blocks/TagsBlock'))
const MediaGalleryBlock = dynamic(() => import('@Components/CMS/Blocks/MediaGalleryBlock'))
const NotifyMeBlock = dynamic(() => import('@Components/CMS/Blocks/NotifyMeBlock'))
const PageGridBlock = dynamic(() => import('@Components/CMS/Blocks/PageGridBlock'))
const PageSliderBlock = dynamic(() => import('@Blocks/PageSliderBlock/PageSliderBlock'))
const ProductAndImageGridBlock = dynamic(() => import('@Components/CMS/Blocks/ProductAndImageGridBlock'))
const ProductDisplayBlock = dynamic(() => import('@Components/CMS/Blocks/ProductDisplayBlock'))
const ProductGridSelectorBlock = dynamic(() => import('@Components/CMS/Blocks/ProductGridSelectorBlock'))
const ProductListBlock = dynamic(() => import('@Blocks/ProductList'))
const ProductSliderBlock = dynamic(() => import('@Components/CMS/Blocks/ProductSliderBlock'))
const QuoteBlock = dynamic(() => import('@Components/CMS/Blocks/QuoteBlock'))
const SearchResultsBlock = dynamic(() => import('@Components/CMS/Blocks/SearchResultsBlock'))
const ShippingBlock = dynamic(() => import('@Components/CMS/Blocks/ShippingBlock'))
const ShippingMatrixBlock = dynamic(() => import('@Components/CMS/Blocks/ShippingMatrixBlock'))
const SubscribeBlock = dynamic(() => import('@Components/CMS/Blocks/SubscribeBlock'))
const TextBlock = dynamic(() => import('@Components/CMS/Blocks/TextBlock'))
const TilesBlock = dynamic(() => import('@Blocks/Tiles'))
const TitleBlock = dynamic(() => import('@Components/CMS/Blocks/TitleBlock'))
const TrackingDisplayBlock = dynamic(() => import('@Components/CMS/Blocks/TrackingDisplayBlock'))
const UrgencyTimerBlock = dynamic(() => import('@Components/CMS/Blocks/UrgencyTimerBlock'))
const USPBlock = dynamic(() => import('@Blocks/USP'))
const VideoBlock = dynamic(() => import('@Components/CMS/Blocks/VideoBlock'))
const WishListBlock = dynamic(() => import('@Components/CMS/Blocks/WishListBlock'))

const blockToComponentMap: BlockToComponentMap = {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define,max-len
  accordionListBlock: (block: AccordionListBlockData, { id, isInModal }) => <AccordionListBlock key={id} accordionListBlock={block} id={id} isInModal={isInModal} />,
  activeCampaignBlock: (block: ActiveCampaignBlockData, { id }) => <ActiveCampaignBlock key={id} activeCampaignBlock={block} id={id} />,
  buttonLinkBlock: (block: Block, { id }) => <LinkBlock key={id} id={id} linkBlock={block} />,
  buyButtonBlock: (block: Block, { id }) => <BuyButtonBlock key={id} buyButtonBlock={block} id={id} />,
  categoryListBlock: (block: Block, { id }) => <CategoryListBlock key={id} categoryListBlock={block} id={id} />,
  clubSignupBlock: (block: Block, { id, presentation }) => <ClubSignupBlock key={id} clubSignupBlock={block} id={id} presentation={presentation} />,
  embedCodeBlock: (block: Block, { id }) => <EmbedCodeBlock key={id} embedCodeBlock={block} id={id} />,
  factBlock: (block: Block, { id }) => <FactBlock key={id} factBlock={block} id={id} />,
  galleryBlock: (block: GalleryBlockData, { id }) => <GalleryBlock key={id} galleryBlock={block} id={id} />,
  giftcardDisplayBlock: (block: Block, { id }) => <GiftCardBlock key={id} giftCardBlock={block} />,
  // TODO: Do we need to support linking to the top of a global block?
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  globalBlock: (block: GlobalBlock, { idPrefix, ids }) => getComponentsForBlocks(block.blocks || [], idPrefix, ids),
  infoPanelBlock: (block: InfoPanelBlockData, { id }) => <InfoPanelBlock block={block} id={id} />,
  // eslint-disable-next-line max-len
  imageBlock: (block: Block, { id, index, isInModal, isInjection }) => <ImageBlock key={id} id={id} imageBlock={block} index={index} isInModal={isInModal} isInjection={isInjection} />,
  imageGridBlock: (block: Block, { id }) => <ImageGridBlock key={id} id={id} imageGridBlock={block} />,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define,max-len
  injection: (block: GlobalBlock, { id, idPrefix, ids }) => getComponentsForBlocks(block.blocks || [], trimEnd(`${id}-${idPrefix}`, '-'), ids, false, undefined, true),
  // TODO: Do we need to support linking to a lead magnet block, and if so where would it scroll to?
  leadMagnetBlock: (block: Block, { id }) => <LeadMagnetBlock key={id} leadMagnetBlock={block} />,
  linkBlock: (block: Block, { id }) => <LinkBlock key={id} id={id} linkBlock={block} />,
  lookDisplayBlock: (block: LookDisplayBlockData, { id, presentation }) => <LookDisplayBlock key={id} data={block} id={id} presentation={presentation} />,
  lookListBlock: (block: LookListBlockData, { id, presentation }) => <LookListBlock key={id} data={block} id={id} presentation={presentation} />,
  mediaGalleryBlock: (block: Block, { id, isInjection }) => <MediaGalleryBlock key={id} id={id} isInjection={isInjection} mediaGalleryBlock={block} />,
  notifyMeBlock: (block: Block, { id }) => <NotifyMeBlock key={id} id={id} notifyMeBlock={block} />,
  pageGridBlock: (block: PageGridBlockData, { id }) => <PageGridBlock key={id} id={id} pageGridBlock={block} />,
  pageSliderBlock: (block: PageSliderBlockGraphData, { id }) => <PageSliderBlock key={id} id={id} pageSliderBlock={block} />,
  // eslint-disable-next-line max-len
  productAndImageGridBlock: (block: Block, { id, isInModal }) => <ProductAndImageGridBlock key={id} id={id} isInModal={isInModal} productAndImageGridBlock={block} />,
  productDisplayBlock: (_, { id, presentation }) => <ProductDisplayBlock key={id} id={id} presentation={presentation} />,
  productGridSelectorBlock: (block: ProductGridSelectorBlockData, { id }) => <ProductGridSelectorBlock key={id} id={id} />,
  productListBlock: (block: ProductListBlockData, { id }) => <ProductListBlock key={id} id={id} productListBlock={block} />,
  productSliderBlock: (block: ProductSliderBlockData, { id }) => <ProductSliderBlock key={id} id={id} productSliderBlock={block} />,
  quoteBlock: (block: Block, { id }) => <QuoteBlock key={id} id={id} quoteBlock={block} />,
  searchResultsBlock: (block: Block, { id }) => <SearchResultsBlock key={id} id={id} />,
  splitLineBlock: (block: Block, { id }) => <SplitLine key={id} id={id} />,
  tagsBlock: (block: Block, { id }) => <TagsBlock key={id} id={id} tagBlock={block} />,
  textBlock: (block: Block, { id }) => <TextBlock key={id} id={id} textBlock={block} />,
  // eslint-disable-next-line max-len
  tilesBlock: (block: Block, { id, presentation }) => <TilesBlock key={id} data={block} id={id} presentation={presentation} />,
  titleBlock: (block: Block, { id }) => <TitleBlock key={id} id={id} titleBlock={block} />,
  trackingDisplayBlock: (block: Block, { id }) => <TrackingDisplayBlock key={id} id={id} />,
  trustpilotBlock: (block: Block, { id }) => <Trustpilot key={id} id={id} />,
  urgencyTimerBlock: (block: Block, { id }) => <UrgencyTimerBlock key={id} id={id} urgencyTimerBlock={block} />,
  uspBlock: (block: Block & UspBlock, { id }) => <USPBlock key={id} data={block} id={id} />,
  videoBlock: (block: Block, { id, index, isInjection }) => <VideoBlock key={id} blockIndex={index} id={id} isInjection={isInjection} videoBlock={block} />,
  shippingUrgencyTimerBlock: (block: Block, { id }) => <ShippingBlock key={id} id={id} shippingBlock={block} />,
  shippingMatrixBlock: (block: Block, { id }) => <ShippingMatrixBlock key={id} id={id} shippingMatrixBlock={block} />,
  subscribeBlock: (block: Block, { id, isInModal }) => <SubscribeBlock key={id} id={id} isInModal={isInModal} subscribeBlock={block} />,
  wishListBlock: (block: Block, { id }) => <WishListBlock key={id} id={id} />,
}

const getComponentsForBlocks = (
  blocks: BlockTypeWithGlobal[],
  idPrefix: string,
  ids: Record<string, number> = {},
  shouldFillContainer = false,
  presentation: PagePresentation = PagePresentation.Full,
  isInjection = false,
): ReactNode[] => compact(
  map(
    blocks,
    (block, index) => {
      let id = getBlockID(block)
      // eslint-disable-next-line no-param-reassign
      ids[id] = (ids[id] || 0) + 1

      if (ids[id] > 1) {
        id = `${id}-${ids[id]}`
      }

      if (idPrefix) {
        id = `${idPrefix}-${id}`
      }

      let blockComponent = blockToComponentMap[block.type || '']?.(block, {
        id,
        idPrefix,
        ids,
        index,
        isInModal: presentation === PagePresentation.Modal,
        isInjection,
        presentation,
      })
      const { splitTest } = block

      if (splitTest?.test) {
        /* eslint-disable react/jsx-no-useless-fragment */
        blockComponent = (
          <Experiment
            a={includes(splitTest.groups, SplitTestGroup.A) ? <>{blockComponent}</> : undefined}
            b={includes(splitTest.groups, SplitTestGroup.B) ? <>{blockComponent}</> : undefined}
            c={includes(splitTest.groups, SplitTestGroup.C) ? <>{blockComponent}</> : undefined}
            d={includes(splitTest.groups, SplitTestGroup.D) ? <>{blockComponent}</> : undefined}
            test={splitTest.test as Test}
          />
        )
        /* eslint-enable react/jsx-no-useless-fragment */
      }

      return (
        <BlockContext.Provider
          key={id}
          // eslint-disable-next-line react/jsx-no-constructed-context-values
          value={{
            blockID: block.id || '',
            blockType: block.type || '',
            id,
            shouldFillContainer,
          }}
        >
          {blockComponent}
        </BlockContext.Provider>
      )
    },
  ),
)

export const useBlockComponents = (
  blocks: BlockTypeWithGlobal[],
  idPrefix: string = '',
  shouldFillContainer = false,
  presentation: PagePresentation = PagePresentation.Full,
  isInjection = false,
): ReactNode[] => useMemo(
  () => getComponentsForBlocks(
    blocks,
    idPrefix,
    {},
    shouldFillContainer,
    presentation,
    isInjection,
  ),
  [
    blocks,
    idPrefix,
    shouldFillContainer,
    presentation,
    isInjection,
  ],
)
